import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTenantKey } from './useTenantKey'
import { ConnectorExpiredNotificationType } from '@/lib/workflowErrorCodes'
import { CONNECTOR_LABELS } from '@/components/projects/connectors/connectorConstants'
import type { ChatMessage, MessageAttachment } from '@/types/chat'
import type { ConnectorType } from '@/types'

interface UseConnectorReconnectRetryOptions {
  projectId: string
  chatId: string | null
  messages: ChatMessage[]
  attachmentsByMessageId: Map<string, MessageAttachment[]>
  onResend: (message: string, attachmentIds?: string[]) => void
}

function hasUnsatisfiedExpiredConnectors(
  messages: ChatMessage[],
  afterSortOrder: number,
  reconnectedConnectors: Set<string>,
): boolean {
  for (const m of messages) {
    if (m.sortOrder <= afterSortOrder || m.role !== 'Notification' || !m.toolResultContent) continue
    try {
      const payload = JSON.parse(m.toolResultContent) as { type?: string; connectorType?: string }
      if (payload.type === ConnectorExpiredNotificationType && payload.connectorType && !reconnectedConnectors.has(payload.connectorType)) {
        return true
      }
    } catch {
      continue
    }
  }
  return false
}

export function useConnectorReconnectRetry({
  projectId,
  chatId,
  messages,
  attachmentsByMessageId,
  onResend,
}: UseConnectorReconnectRetryOptions) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  const retriedMessageIdsRef = useRef<Set<string>>(new Set())
  const reconnectedConnectorsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    reconnectedConnectorsRef.current = new Set()
  }, [chatId])

  const resendAfterReconnect = useCallback(
    async (connectorType: ConnectorType) => {
      if (!chatId) return

      reconnectedConnectorsRef.current.add(connectorType)

      const messagesKey = tenantKey('projects', projectId, 'chats', chatId, 'messages')
      await queryClient.refetchQueries({ queryKey: messagesKey })

      const currentMessages = queryClient.getQueryData<ChatMessage[]>(messagesKey) ?? messages
      const latestUserMessage = [...currentMessages].reverse().find((m) => m.role === 'User')
      if (!latestUserMessage) return

      const followups = currentMessages.filter(
        (m) => m.sortOrder > latestUserMessage.sortOrder && (m.role === 'Assistant' || m.role === 'ToolResult'),
      )
      if (followups.length > 0) return

      if (hasUnsatisfiedExpiredConnectors(currentMessages, latestUserMessage.sortOrder, reconnectedConnectorsRef.current)) return

      if (retriedMessageIdsRef.current.has(latestUserMessage.id)) return

      const attachmentIds = attachmentsByMessageId.get(latestUserMessage.id)?.map((a) => a.id)
      if (!latestUserMessage.textContent && !attachmentIds?.length) return

      retriedMessageIdsRef.current.add(latestUserMessage.id)
      reconnectedConnectorsRef.current = new Set()

      onResend(latestUserMessage.textContent ?? '', attachmentIds && attachmentIds.length > 0 ? attachmentIds : undefined)
    },
    [attachmentsByMessageId, chatId, messages, onResend, projectId, queryClient, tenantKey],
  )

  return useCallback(
    (connectorType: ConnectorType) => {
      resendAfterReconnect(connectorType).catch(() => {
        toast.error(`${CONNECTOR_LABELS[connectorType] ?? connectorType} reconnected, but the message could not be retried — send it again.`)
      })
    },
    [resendAfterReconnect],
  )
}
